import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/utils/useAuth";
import { RequestComments } from "@/modules/approvals/RequestComments";
import { FormDataDisplay } from "@/modules/requests/FormDataDisplay";
import { 
  Download, 
  ChevronDown, 
  Settings,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Eye,
  Loader2,
  Building2,
  User,
  Briefcase,
  QrCode,
  Radio,
  CreditCard,
  Copy,
  RotateCcw,
  Shield,
  Send,
  Mail,
  MessageSquare,
  Phone as PhoneIcon,
  Edit2,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Link, useRoute, useLocation, useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { format } from "date-fns";
import { toast } from "sonner";
import QRCode from "qrcode";
import {
  FioriPageHeader,
  FioriFilterBar,
  FioriTable,
  FioriStatusBadge,
} from "@/components/fiori";
import type { FioriColumn } from "@/components/fiori";

// typeLabels is now built inside the component using t() — see below

// statusConfig is now built inside the component using t() — see below

// entryMethodConfig is now built inside the component using t() — see below

export default function Requests() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Short abbreviations for table badge (keeps column narrow)
  const typeLabels = useMemo<Record<string, string>>(() => ({
    admin_visit: t('requests.types.adminVisitShort', 'Admin'),
    tep: t('requests.types.tepShort', 'TEP'),
    work_permit: t('requests.types.workPermitShort', 'WP'),
    mhv: t('requests.types.mhvShort', 'MHV'),
    mop: t('requests.types.mopShort', 'MOP'),
  }), [t]);
  // Full names for filter dropdown and detail view
  const typeFullLabels = useMemo<Record<string, string>>(() => ({
    admin_visit: t('requests.types.adminVisit', 'Admin Visit'),
    tep: t('requests.types.tep', 'TEP (Temporary Entry Permit)'),
    work_permit: t('requests.types.workPermit', 'WP (Work Permit)'),
    mhv: t('requests.types.mhv', 'MHV (Material Handling Vehicle)'),
    mop: t('requests.types.mop', 'MOP (Method of Procedure)'),
  }), [t]);

  const statusConfig = useMemo<Record<string, { label: string; icon: React.ReactNode; color: string }>>(() => ({
    draft: { label: t('common.draft', 'Draft'), icon: <FileText className="h-3 w-3" />, color: "text-[#6B6B6B] bg-[#F5F5F5]" },
    pending_approval: { label: t('approvals.pendingApproval', 'Pending Approval'), icon: <Clock className="h-3 w-3" />, color: "text-[#D97706] bg-[#FEF3C7]" },
    approved: { label: t('common.approved', 'Approved'), icon: <CheckCircle2 className="h-3 w-3" />, color: "text-[#059669] bg-[#D1FAE5]" },
    rejected: { label: t('common.rejected', 'Rejected'), icon: <XCircle className="h-3 w-3" />, color: "text-[#FF6B6B] bg-[#FFE5E5]" },
    need_clarification: { label: t('approvals.needClarification', 'Need Clarification'), icon: <MessageSquare className="h-3 w-3" />, color: "text-[#B45309] bg-[#FEF3C7]" },
    // internal statuses kept for badge rendering only (not shown in filter dropdown)
    pending_l1: { label: t('approvals.pendingL1', 'Pending L1'), icon: <Clock className="h-3 w-3" />, color: "text-[#D97706] bg-[#FEF3C7]" },
    pending_manual: { label: t('approvals.pendingManual', 'Pending Manual'), icon: <AlertCircle className="h-3 w-3" />, color: "text-[#5B2C93] bg-[#E8DCF5]" },
    cancelled: { label: t('common.cancelled', 'Cancelled'), icon: <XCircle className="h-3 w-3" />, color: "text-[#2C2C2C] bg-[#F5F5F5]" },
    expired: { label: t('common.expired', 'Expired'), icon: <Clock className="h-3 w-3" />, color: "text-[#2C2C2C] bg-[#F5F5F5]" },
  }), [t]);
  // Only these 5 statuses appear in the filter dropdown
  const filterableStatuses = ['draft', 'pending_approval', 'approved', 'rejected', 'need_clarification'];

  const entryMethodConfig = useMemo<Record<string, { label: string; icon: React.ReactNode; color: string }>>(() => ({
    qr_code: { label: t('requests.qrCode', 'QR Code'), icon: <QrCode className="h-4 w-4" />, color: "text-[#059669] bg-[#D1FAE5]" },
    rfid: { label: t('requests.rfidTag', 'RFID Tag'), icon: <Radio className="h-4 w-4" />, color: "text-[#5B2C93] bg-[#E8DCF5]" },
    card: { label: t('requests.accessCard', 'Access Card'), icon: <CreditCard className="h-4 w-4" />, color: "text-[#5B2C93] bg-[#E8DCF5]" },
  }), [t]);
  const isAdmin = user?.role === "admin";
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<number | null>(null);
  const [, setLocation] = useLocation();
  // routeParams picks up :id when rendered inside <Route path="/requests/:id" nest>
  const routeParams = useParams<{ id?: string }>();
  // matchId / matchParams covers direct non-nested navigation to /requests/:id
  const [matchId, matchParams] = useRoute("/requests/:id");

  // Deep-link: auto-open dialog when URL is /requests/:id
  useEffect(() => {
    const idStr = routeParams?.id ?? (matchId ? matchParams?.id : undefined);
    if (idStr) {
      const numId = parseInt(idStr, 10);
      if (!isNaN(numId)) setSelectedRequest(numId);
    }
  }, [routeParams?.id, matchId, matchParams?.id]);
  
  const [editAccessMethodOpen, setEditAccessMethodOpen] = useState(false);
  const [newEntryMethod, setNewEntryMethod] = useState<"qr_code" | "rfid" | "card">("qr_code");
  const [newRfidTag, setNewRfidTag] = useState("");
  const [newCardNumber, setNewCardNumber] = useState("");
  const [qrCodeImage, setQrCodeImage] = useState<string>("");
  const [resendingChannel, setResendingChannel] = useState<string | null>(null);
  const [resubmitComment, setResubmitComment] = useState("");
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  
  const { data, isLoading, isFetching, refetch } = trpc.requests.getAll.useQuery({
    status: statusFilter.length > 0 ? statusFilter as any : undefined,
    type: typeFilter.length > 0 ? typeFilter as any : undefined,
    limit: 100,
  });
  
  const { data: requestDetail, isLoading: detailLoading, refetch: refetchDetail } = trpc.requests.getById.useQuery(
    { id: selectedRequest! },
    { enabled: !!selectedRequest }
  );
  
  const resubmitMutation = trpc.requests.resubmitAfterClarification.useMutation({
    onSuccess: () => {
      toast.success(t('requests.resubmitSuccess', 'Request re-submitted successfully'));
      setResubmitDialogOpen(false);
      setResubmitComment("");
      refetch();
      refetchDetail();
    },
    onError: (error) => toast.error(t('requests.resubmitFailed', 'Failed to re-submit'), { description: error.message }),
  });

  const updateAccessMethod = trpc.requests.updateAccessMethod.useMutation({
    onSuccess: async (result: any) => {
      toast.success(t('requests.accessMethodUpdated', 'Access method updated'));
      if (result.entryMethod === "qr_code" && result.qrCodeData) {
        try {
          const qrDataUrl = await QRCode.toDataURL(result.qrCodeData, { width: 300, margin: 2, color: { dark: "#000000", light: "#ffffff" } });
          setQrCodeImage(qrDataUrl);
        } catch (err) { console.error("Failed to generate QR code:", err); }
      }
      refetchDetail();
      setEditAccessMethodOpen(false);
    },
    onError: (error) => toast.error(t('requests.accessMethodUpdateFailed', 'Failed to update access method'), { description: error.message }),
  });
  
  const requests = data?.requests || [];
  
  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        req.requestNumber.toLowerCase().includes(query) ||
        req.visitorName.toLowerCase().includes(query) ||
        req.visitorCompany?.toLowerCase().includes(query) ||
        req.siteName?.toLowerCase().includes(query)
      );
    });
  }, [requests, searchQuery]);
  
  const handleOpenAccessMethodEdit = () => {
    if (requestDetail?.accessMethod) {
      setNewEntryMethod(requestDetail.accessMethod.entryMethod as any || "qr_code");
      setNewRfidTag(requestDetail.accessMethod.rfidTag || "");
      setNewCardNumber(requestDetail.accessMethod.cardNumber || "");
    }
    setEditAccessMethodOpen(true);
  };
  
  const handleSaveAccessMethod = () => {
    if (!selectedRequest) return;
    if (newEntryMethod === "rfid" && !newRfidTag.trim()) { toast.error(t('requests.enterRfidTag', 'Please enter the RFID tag number')); return; }
    if (newEntryMethod === "card" && !newCardNumber.trim()) { toast.error(t('requests.enterCardNumber', 'Please enter the card number')); return; }
    updateAccessMethod.mutate({
      requestId: selectedRequest,
      entryMethod: newEntryMethod,
      rfidTag: newEntryMethod === "rfid" ? newRfidTag : undefined,
      cardNumber: newEntryMethod === "card" ? newCardNumber : undefined,
    });
  };
  
  const handleRegenerateQr = () => {
    if (!selectedRequest) return;
    updateAccessMethod.mutate({ requestId: selectedRequest, entryMethod: "qr_code", regenerateQr: true });
  };
  
  const handleCopyQrData = () => {
    if (requestDetail?.accessMethod?.qrCodeData) {
      navigator.clipboard.writeText(requestDetail.accessMethod.qrCodeData);
      toast.success("QR code data copied to clipboard");
    }
  };
  
  const handleResendCredentials = async (channel: "email" | "sms" | "whatsapp") => {
    if (!requestDetail) return;
    setResendingChannel(channel);
    try {
      const visitorEmail = requestDetail.visitorEmail;
      const visitorPhone = requestDetail.visitorPhone;
      const accessMethod = requestDetail.accessMethod;
      
      if (channel === "email") {
        if (!visitorEmail) { toast.error(t('requests.noEmailAvailable', 'No email address available')); setResendingChannel(null); return; }
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success(t('requests.credentialsSentEmail', 'Access credentials sent via Email'), { description: `Sent to ${visitorEmail}` });
      } else if (channel === "sms") {
        if (!visitorPhone) { toast.error(t('requests.noPhoneAvailable', 'No phone number available')); setResendingChannel(null); return; }
        await new Promise(resolve => setTimeout(resolve, 1000));
        toast.success(t('requests.credentialsSentSms', 'Access credentials sent via SMS'), { description: `Sent to ${visitorPhone}` });
      } else if (channel === "whatsapp") {
        if (!visitorPhone) { toast.error(t('requests.noPhoneAvailable', 'No phone number available')); setResendingChannel(null); return; }
        const message = encodeURIComponent(
          `Your access credentials for ${requestDetail.siteName || 'the facility'}:\n\n` +
          `Request: ${requestDetail.requestNumber}\n` +
          `Valid: ${requestDetail.startDate ? format(new Date(requestDetail.startDate), "MMM d, yyyy") : "N/A"} - ${requestDetail.endDate ? format(new Date(requestDetail.endDate), "MMM d, yyyy") : "N/A"}\n` +
          (accessMethod?.entryMethod === "qr_code" ? `\nAccess Code: ${accessMethod.qrCodeData}` : "") +
          (accessMethod?.entryMethod === "rfid" ? `\nRFID Tag: ${accessMethod.rfidTag}` : "") +
          (accessMethod?.entryMethod === "card" ? `\nCard Number: ${accessMethod.cardNumber}` : "")
        );
        const phone = visitorPhone.replace(/[^0-9]/g, "");
        window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
        toast.success(t('requests.whatsappOpened', 'WhatsApp opened'));
      }
    } catch { toast.error(t('requests.credentialsSendFailed', 'Failed to send credentials')); }
    finally { setResendingChannel(null); }
  };
  
  const generateQrImage = async (qrData: string) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(qrData, { width: 200, margin: 2, color: { dark: "#000000", light: "#ffffff" } });
      setQrCodeImage(qrDataUrl);
    } catch (err) { console.error("Failed to generate QR code:", err); }
  };
  
  if (requestDetail?.accessMethod?.qrCodeData && !qrCodeImage) {
    generateQrImage(requestDetail.accessMethod.qrCodeData);
  }

  const toggleStatus = (key: string) => setStatusFilter(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  const toggleType = (key: string) => setTypeFilter(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]);

  const activeFilters = [
    ...statusFilter.map(s => ({ key: `status-${s}`, label: `${t('common.status')}: ${statusConfig[s]?.label || s}`, onRemove: () => toggleStatus(s) })),
    ...typeFilter.map(tp => ({ key: `type-${tp}`, label: `${t('common.type')}: ${typeFullLabels[tp] || tp}`, onRemove: () => toggleType(tp) })),
  ];

  type RequestItem = typeof requests[number];

  const columns = useMemo<FioriColumn<RequestItem>[]>(() => [
    {
      key: "requestNumber", header: t('requests.requestId'), width: "180px",
      render: (req) => (
        <span className="font-mono text-sm font-medium text-[#5B2C93] cursor-pointer hover:underline truncate block max-w-[180px]"
          title={req.requestNumber}
          onClick={(e) => { e.stopPropagation(); setSelectedRequest(req.id); setQrCodeImage(""); }}>
          {req.requestNumber}
        </span>
      ),
    },
    {
      key: "status", header: t('common.status'), width: "160px",
      render: (req) => {
        const status = statusConfig[req.status] || statusConfig.draft;
        return (
          <Badge variant="secondary" className={`${status.color} gap-1`}>
            {status.icon}
            {status.label}
          </Badge>
        );
      },
    },
    {
      key: "type", header: t('common.type'), width: "90px",
      render: (req) => (
        <span
          className="text-xs font-medium text-[#6B6B6B] bg-[#F5F5F5] px-2 py-0.5 rounded whitespace-nowrap"
          title={typeFullLabels[req.type] || req.type}
        >
          {typeLabels[req.type] || req.type}
        </span>
      ),
    },
    {
      key: "visitor", header: t('requests.visitor'), width: "200px",
      render: (req) => (
        <div className="min-w-0">
          <span className="font-medium text-[#2C2C2C] truncate block">{req.visitorName}</span>
          {req.visitorCompany && <p className="text-xs text-[#6B6B6B] mt-0.5 truncate">{req.visitorCompany}</p>}
        </div>
      ),
    },
    {
      key: "site", header: t('requests.site'), width: "160px",
      render: (req) => <span className="text-[#6B6B6B]">{req.siteName || "—"}</span>,
    },
    {
      key: "dateRange", header: t('requests.schedule'), width: "180px",
      render: (req) => (
        <span className="text-xs text-[#6B6B6B]">{req.startDate} → {req.endDate}</span>
      ),
    },
    {
      key: "created", header: t('requests.createdAt'), width: "120px",
      render: (req) => (
        <span className="text-xs text-[#6B6B6B]">
          {req.createdAt ? format(new Date(req.createdAt), "MMM dd, yyyy") : "—"}
        </span>
      ),
    },
    {
      key: "actions", header: "", width: "50px", align: "end",
      render: (req) => (
        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#B0B0B0] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
          onClick={(e) => { e.stopPropagation(); setSelectedRequest(req.id); setQrCodeImage(""); }}>
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ], [t, statusConfig, typeLabels, typeFullLabels]);

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      <FioriPageHeader
        title={t('requests.title')}
        subtitle={t('requests.allRequests')}
        icon={<FileText className="h-5 w-5" />}
        count={filteredRequests.length}
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => toast.info("Export feature coming soon")} className="gap-1.5 text-[#6B6B6B] border-[#E0E0E0]">
              <Download className="h-3.5 w-3.5" /> {t('common.export')}
            </Button>
            <Link href="/requests/new">
              <Button size="sm" className="gap-1.5 bg-[#5B2C93] hover:bg-[#3D1C5E]">
                <Plus className="h-4 w-4" /> {t('requests.newRequest')}
              </Button>
            </Link>
          </>
        }
      />

      <FioriFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('requests.searchPlaceholder')}
        activeFilters={activeFilters}
        onClearAll={() => { setStatusFilter([]); setTypeFilter([]); }}
        filters={
          <>
            {/* Status multi-select */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full sm:w-[170px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0] justify-between font-normal">
                  <span className="truncate">
                    {statusFilter.length === 0 ? t('common.all') + ' ' + t('common.status') : statusFilter.length === 1 ? (statusConfig[statusFilter[0]]?.label || statusFilter[0]) : `${statusFilter.length} ${t('common.status')}`}
                  </span>
                  <ChevronDown className="h-4 w-4 ml-1 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[200px] p-1" onClick={e => e.stopPropagation()}>
                {filterableStatuses.map((key) => { const config = statusConfig[key]; return (
                  <div key={key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F5F5F5] cursor-pointer" onClick={() => toggleStatus(key)}>
                    <Checkbox checked={statusFilter.includes(key)} onCheckedChange={() => toggleStatus(key)} className="pointer-events-none" />
                    <span className="text-sm">{config.label}</span>
                  </div>
                ); })}
                {statusFilter.length > 0 && (
                  <div className="border-t mt-1 pt-1">
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F5F5F5] cursor-pointer text-[#5B2C93]" onClick={() => setStatusFilter([])}>
                      <span className="text-xs font-medium">{t('common.clearSelection', 'Clear selection')}</span>
                    </div>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Type multi-select */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full sm:w-[160px] h-9 text-sm bg-[#F5F5F5] border-[#E0E0E0] justify-between font-normal">
                  <span className="truncate">
                    {typeFilter.length === 0 ? t('common.all') + ' ' + t('common.type') : typeFilter.length === 1 ? (typeFullLabels[typeFilter[0]] || typeFilter[0]) : `${typeFilter.length} ${t('common.type')}`}
                  </span>
                  <ChevronDown className="h-4 w-4 ml-1 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[180px] p-1" onClick={e => e.stopPropagation()}>
                {Object.entries(typeFullLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F5F5F5] cursor-pointer" onClick={() => toggleType(key)}>
                    <Checkbox checked={typeFilter.includes(key)} onCheckedChange={() => toggleType(key)} className="pointer-events-none" />
                    <span className="text-sm">{label}</span>
                  </div>
                ))}
                {typeFilter.length > 0 && (
                  <div className="border-t mt-1 pt-1">
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F5F5F5] cursor-pointer text-[#5B2C93]" onClick={() => setTypeFilter([])}>
                      <span className="text-xs font-medium">{t('common.clearSelection', 'Clear selection')}</span>
                    </div>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <div className="mt-4">
        <FioriTable
          columns={columns}
          data={filteredRequests}
          isLoading={isLoading}
          rowKey={(req) => req.id}
          emptyIcon={<FileText className="h-10 w-10" />}
          emptyTitle={t('common.noResults')}
          emptyDescription={t('requests.searchPlaceholder')}
          footerInfo={`${t('table.showing')} ${filteredRequests.length} ${t('table.of')} ${requests.length}`}
        />
      </div>

      {/* Request Detail Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => { if (!open) { setSelectedRequest(null); setQrCodeImage(""); setLocation("/requests"); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#5B2C93]" />
              {t('requests.requestDetails')}
            </DialogTitle>
            <DialogDescription>{requestDetail?.requestNumber}</DialogDescription>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#5B2C93]" />
            </div>
          ) : requestDetail ? (
            <div className="space-y-5">
              {/* Status & Type */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className={`${statusConfig[requestDetail.status]?.color} gap-1 text-sm px-3 py-1`}>
                    {statusConfig[requestDetail.status]?.icon}
                    {statusConfig[requestDetail.status]?.label}
                  </Badge>
                  <span className="text-xs font-medium text-[#6B6B6B] bg-[#F5F5F5] px-2 py-0.5 rounded">
                    {typeFullLabels[requestDetail.type] || requestDetail.type}
                  </span>
                </div>
                {requestDetail.status === "draft" && (
                  <Link href={`/requests/${requestDetail.id}/edit`}>
                    <Button variant="outline" size="sm" className="gap-2 border-[#E0E0E0]">
                      <Edit2 className="h-4 w-4" /> {t('requests.editDraft', 'Edit Draft')}
                    </Button>
                  </Link>
                )}
                {requestDetail.status === "need_clarification" && (
                  <Button variant="outline" size="sm" className="gap-2 border-[#B45309] text-[#B45309]" onClick={() => setResubmitDialogOpen(true)}>
                    <Send className="h-4 w-4" /> {t('requests.respondResubmit', 'Respond & Re-submit')}
                  </Button>
                )}
              </div>

              {/* Need Clarification Banner */}
              {requestDetail.status === "need_clarification" && (
                <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-lg p-4 space-y-2">
                  <h4 className="font-medium flex items-center gap-2 text-[#B45309]">
                    <MessageSquare className="h-4 w-4" /> {t('requests.clarificationRequested', 'Clarification Requested')}
                  </h4>
                  <p className="text-sm text-[#92400E]">
                    {t('requests.clarificationDesc', 'An approver has requested clarification on this request. Please review the comments below and respond to re-submit your request.')}
                  </p>
                </div>
              )}
              
              {/* Access Method Section */}
              {requestDetail.status === "approved" && (
                <div className="bg-[#E8F9F8] border border-[#4ECDC4] rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2 text-[#0D9488]">
                      <Shield className="h-4 w-4" /> {t('requests.accessMethod', 'Access Method')}
                    </h4>
                    {isAdmin && (
                      <Button variant="outline" size="sm" onClick={handleOpenAccessMethodEdit} className="border-[#4ECDC4] text-[#0D9488]">
                        <Settings className="h-4 w-4 mr-2" /> {t('requests.changeMethod', 'Change Method')}
                      </Button>
                    )}
                  </div>
                  
                  {requestDetail.accessMethod?.entryMethod ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className={`${entryMethodConfig[requestDetail.accessMethod.entryMethod]?.color} gap-2 text-sm px-3 py-2`}>
                          {entryMethodConfig[requestDetail.accessMethod.entryMethod]?.icon}
                          {entryMethodConfig[requestDetail.accessMethod.entryMethod]?.label}
                        </Badge>
                        {requestDetail.accessMethod.accessGrantedByName && (
                          <span className="text-sm text-[#6B6B6B]">{t('requests.grantedBy', 'Granted by')} {requestDetail.accessMethod.accessGrantedByName}</span>
                        )}
                      </div>
                      
                      {requestDetail.accessMethod.entryMethod === "qr_code" && requestDetail.accessMethod.qrCodeData && (
                        <div className="flex items-start gap-4 p-3 bg-white rounded-lg border border-[#E0E0E0]">
                          {qrCodeImage && <img src={qrCodeImage} alt="Access QR Code" className="w-32 h-32" />}
                          <div className="flex-1 space-y-2">
                            <div>
                              <Label className="text-xs text-[#6B6B6B]">{t('requests.qrCodeData', 'QR Code Data')}</Label>
                              <div className="flex items-center gap-2">
                                <code className="text-sm font-mono bg-[#F5F5F5] px-2 py-1 rounded">{requestDetail.accessMethod.qrCodeData}</code>
                                <Button variant="ghost" size="sm" onClick={handleCopyQrData}><Copy className="h-4 w-4" /></Button>
                              </div>
                            </div>
                            {isAdmin && (
                              <Button variant="outline" size="sm" onClick={handleRegenerateQr} className="border-[#E0E0E0]">
                                <RotateCcw className="h-4 w-4 mr-2" /> {t('requests.regenerateQr', 'Regenerate QR')}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {requestDetail.accessMethod.entryMethod === "rfid" && requestDetail.accessMethod.rfidTag && (
                        <div className="p-3 bg-white rounded-lg border border-[#E0E0E0]">
                          <Label className="text-xs text-[#6B6B6B]">{t('requests.rfidTagNumber', 'RFID Tag Number')}</Label>
                          <p className="font-mono text-sm">{requestDetail.accessMethod.rfidTag}</p>
                        </div>
                      )}
                      
                      {requestDetail.accessMethod.entryMethod === "card" && requestDetail.accessMethod.cardNumber && (
                        <div className="p-3 bg-white rounded-lg border border-[#E0E0E0]">
                          <Label className="text-xs text-[#6B6B6B]">{t('requests.cardNumberLabel', 'Card Number')}</Label>
                          <p className="font-mono text-sm">{requestDetail.accessMethod.cardNumber}</p>
                        </div>
                      )}
                      
                      <div className="pt-3 border-t border-[#4ECDC4]">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#0D9488]">{t('requests.sendCredentials', 'Send access credentials to visitor')}</span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="gap-2 border-[#E0E0E0]" disabled={!!resendingChannel}>
                                {resendingChannel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                {resendingChannel ? t('requests.sending', 'Sending...') : t('requests.resend', 'Resend')}
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleResendCredentials("email")} className="gap-2"><Mail className="h-4 w-4" /> {t('requests.sendViaEmail', 'Send via Email')}</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResendCredentials("sms")} className="gap-2"><MessageSquare className="h-4 w-4" /> {t('requests.sendViaSms', 'Send via SMS')}</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResendCredentials("whatsapp")} className="gap-2"><PhoneIcon className="h-4 w-4" /> {t('requests.sendViaWhatsapp', 'Send via WhatsApp')}</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-[#6B6B6B] mb-3">{t('requests.noAccessMethod', 'No access method assigned yet')}</p>
                      <Button variant="outline" size="sm" onClick={handleOpenAccessMethodEdit}>{t('requests.assignAccessMethod', 'Assign Access Method')}</Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Visitor Info */}
              <div className="bg-[#FAFAFA] border border-[#E0E0E0] rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-[#F5F5F5] border-b border-[#E0E0E0]">
                  <h4 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-[#5B2C93]" /> {t('requests.visitorInformation')}
                  </h4>
                </div>
                <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-[#6B6B6B] text-xs">{t('common.name')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.visitorName}</p></div>
                  <div><span className="text-[#6B6B6B] text-xs">{t('requests.form.company')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.visitorCompany || "—"}</p></div>
                  <div><span className="text-[#6B6B6B] text-xs">{t('requests.form.idType')}</span><p className="font-medium text-[#2C2C2C] capitalize">{requestDetail.visitorIdType?.replace("_", " ")}</p></div>
                  <div><span className="text-[#6B6B6B] text-xs">{t('requests.form.idNumber')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.visitorIdNumber}</p></div>
                  <div><span className="text-[#6B6B6B] text-xs">{t('common.phone')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.visitorPhone || "—"}</p></div>
                  <div><span className="text-[#6B6B6B] text-xs">{t('common.email')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.visitorEmail || "—"}</p></div>
                </div>
              </div>
              
              {/* Visit Details */}
              <div className="bg-[#FAFAFA] border border-[#E0E0E0] rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-[#F5F5F5] border-b border-[#E0E0E0]">
                  <h4 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-[#5B2C93]" /> {t('requests.accessDetails')}
                  </h4>
                </div>
                <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-[#6B6B6B] text-xs">{t('requests.site')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.siteName || "—"}</p></div>
                  <div><span className="text-[#6B6B6B] text-xs">{t('requests.startDate')} → {t('requests.endDate')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.startDate} → {requestDetail.endDate}</p></div>
                  <div><span className="text-[#6B6B6B] text-xs">{t('common.time')}</span><p className="font-medium text-[#2C2C2C]">{requestDetail.startTime || "00:00"} — {requestDetail.endTime || "23:59"}</p></div>
                </div>
                {requestDetail.purpose && (
                  <div className="px-4 pb-4">
                    <span className="text-[#6B6B6B] text-xs">{t('requests.purpose')}</span>
                    <p className="text-sm mt-1 text-[#2C2C2C]">{requestDetail.purpose}</p>
                  </div>
                )}
              </div>
              
              {/* Zones */}
              {requestDetail.zones && requestDetail.zones.length > 0 && (
                <div className="bg-[#FAFAFA] border border-[#E0E0E0] rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-[#F5F5F5] border-b border-[#E0E0E0]">
                    <h4 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t('requests.zone')}</h4>
                  </div>
                  <div className="p-4 flex flex-wrap gap-2">
                    {requestDetail.zones.map((zone: any) => (
                      <Badge key={zone.id} variant="outline" className="gap-1 border-[#E0E0E0]">
                        {zone.name} <span className="text-xs text-[#6B6B6B]">({zone.securityLevel})</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Dynamic Form Data */}
              {requestDetail.formData && requestDetail.selectedTypeIds && (
                <FormDataDisplay
                  formData={requestDetail.formData as Record<string, any>}
                  selectedTypeIds={requestDetail.selectedTypeIds as number[]}
                  categoryId={requestDetail.categoryId}
                  compact={true}
                />
              )}
              
              {/* Download Form PDF (only for approved requests) */}
              {requestDetail.status === "approved" && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-[#5B2C93] hover:bg-[#E8DCF5] border-[#E0E0E0]"
                    onClick={() => {
                      const token = localStorage.getItem("app_session_token");
                      const url = `/api/forms/pdf/${requestDetail.id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
                      window.open(url, '_blank');
                    }}
                  >
                    <FileText className="h-4 w-4" /> {t('reports.exportPdf')}
                  </Button>
                </div>
              )}
              
              {/* Comments Section */}
              {requestDetail.id && (
                <div className="pt-2">
                  <RequestComments requestId={requestDetail.id} />
                </div>
              )}
              
              {/* Approval History */}
              {/* Escalations */}
              {requestDetail.escalations && requestDetail.escalations.length > 0 && (
                <div className="bg-[#FAFAFA] border border-[#E0E0E0] rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-[#F5F5F5] border-b border-[#E0E0E0]">
                    <h4 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide flex items-center gap-2">
                      <AlertCircle className="h-3.5 w-3.5 text-[#FF6B6B]" /> {t('requests.escalations', 'Escalations')} ({requestDetail.escalations.length})
                    </h4>
                  </div>
                  <div className="p-4 space-y-3">
                    {requestDetail.escalations.map((escalation: any) => {
                      const severityColors: Record<string, string> = {
                        low: "bg-[#D1FAE5] text-[#059669]",
                        medium: "bg-[#FEF3C7] text-[#D97706]",
                        high: "bg-[#FECACA] text-[#DC2626]",
                        critical: "bg-[#FCA5A5] text-[#991B1B]",
                      };
                      return (
                        <div key={escalation.id} className="border border-[#E0E0E0] rounded-lg p-3 bg-white">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${severityColors[escalation.severity] || severityColors.medium}`}>
                                {escalation.severity.toUpperCase()}
                              </span>
                              <span className="text-xs font-medium text-[#6B6B6B] bg-[#F5F5F5] px-2 py-0.5 rounded">
                                {escalation.escalationType.replace(/_/g, " ")}
                              </span>
                            </div>
                            <span className="text-xs text-[#6B6B6B]">{escalation.status}</span>
                          </div>
                          <p className="text-sm text-[#2C2C2C] mb-2">{escalation.description}</p>
                          <div className="flex items-center justify-between text-xs text-[#6B6B6B]">
                            <span>{t('requests.raisedBy', 'Raised by')}: {escalation.raisedByName}</span>
                            <span>{new Date(escalation.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {requestDetail.approvals && requestDetail.approvals.length > 0 && (
                <div className="bg-[#FAFAFA] border border-[#E0E0E0] rounded-lg overflow-hidden">
                  <div className="px-4 py-2.5 bg-[#F5F5F5] border-b border-[#E0E0E0]">
                    <h4 className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide flex items-center gap-2">
                      <Briefcase className="h-3.5 w-3.5 text-[#5B2C93]" /> {t('approvals.approvalHistory', 'Approval History')}
                    </h4>
                  </div>
                  <div className="p-4 space-y-2">
                    {requestDetail.approvals.map((approval: any) => (
                      <div key={approval.id} className="flex items-center justify-between py-2 border-b border-[#F0F0F0] last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-medium text-[#6B6B6B] bg-[#F5F5F5] px-2 py-0.5 rounded uppercase">{approval.stage}</span>
                          <span className="text-sm text-[#2C2C2C]">{approval.approverName || t('common.pending', 'Pending')}</span>
                        </div>
                        <FioriStatusBadge status={approval.status} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Access Method Dialog */}
      <Dialog open={editAccessMethodOpen} onOpenChange={setEditAccessMethodOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-[#5B2C93]" /> {t('requests.changeAccessMethod', 'Change Access Method')}</DialogTitle>
            <DialogDescription>{t('requests.changeAccessMethodDesc', 'Update the access method for this approved request')}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('requests.selectEntryMethod', 'Select Entry Method')}</Label>
              <RadioGroup value={newEntryMethod} onValueChange={(v) => setNewEntryMethod(v as any)} className="grid gap-3">
                {[
                  { value: "qr_code", label: t('requests.qrCode', 'QR Code'), desc: t('requests.qrCodeDesc', 'Generate a unique QR code for visitor check-in'), icon: <QrCode className="h-5 w-5 text-[#0D9488]" />, bg: "bg-[#E8F9F8]", activeBorder: "border-[#4ECDC4] bg-[#E8F9F8]" },
                  { value: "rfid", label: t('requests.rfidTag', 'RFID Tag'), desc: t('requests.rfidTagDesc', 'Assign an RFID tag for contactless access'), icon: <Radio className="h-5 w-5 text-[#5B2C93]" />, bg: "bg-[#E8DCF5]", activeBorder: "border-[#5B2C93] bg-[#E8DCF5]" },
                  { value: "card", label: t('requests.accessCard', 'Access Card'), desc: t('requests.accessCardDesc', 'Issue a physical access card'), icon: <CreditCard className="h-5 w-5 text-[#5B2C93]" />, bg: "bg-[#E8DCF5]", activeBorder: "border-[#5B2C93] bg-[#E8DCF5]" },
                ].map((method) => (
                  <div key={method.value} className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer ${newEntryMethod === method.value ? method.activeBorder : "border-[#E0E0E0] hover:bg-[#FAFAFA]"}`}>
                    <RadioGroupItem value={method.value} id={`edit_${method.value}`} />
                    <Label htmlFor={`edit_${method.value}`} className="flex items-center gap-3 cursor-pointer flex-1">
                      <div className={`p-2 ${method.bg} rounded-lg`}>{method.icon}</div>
                      <div>
                        <p className="font-medium text-[#2C2C2C]">{method.label}</p>
                        <p className="text-xs text-[#6B6B6B]">{method.desc}</p>
                      </div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            {newEntryMethod === "rfid" && (
              <div className="space-y-1.5">
                <Label htmlFor="newRfidTag" className="text-sm">{t('requests.rfidTagNumber', 'RFID Tag Number')}</Label>
                <Input id="newRfidTag" placeholder={t('requests.enterRfidTagPlaceholder', 'Enter RFID tag number...')} value={newRfidTag} onChange={(e) => setNewRfidTag(e.target.value)} />
              </div>
            )}
            
            {newEntryMethod === "card" && (
              <div className="space-y-1.5">
                <Label htmlFor="newCardNumber" className="text-sm">{t('requests.cardNumberLabel', 'Card Number')}</Label>
                <Input id="newCardNumber" placeholder={t('requests.enterCardNumberPlaceholder', 'Enter access card number...')} value={newCardNumber} onChange={(e) => setNewCardNumber(e.target.value)} />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAccessMethodOpen(false)} className="border-[#E0E0E0]">{t('common.cancel')}</Button>
            <Button onClick={handleSaveAccessMethod} disabled={updateAccessMethod.isPending} className="bg-[#5B2C93] hover:bg-[#3D1C5E]">
              {updateAccessMethod.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {t('common.saveChanges', 'Save Changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-submit After Clarification Dialog */}
      <Dialog open={resubmitDialogOpen} onOpenChange={setResubmitDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-[#B45309]" /> {t('requests.respondResubmit', 'Respond & Re-submit')}</DialogTitle>
            <DialogDescription>{t('requests.respondResubmitDesc', 'Provide your response to the clarification request and re-submit for approval')}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('requests.yourResponse', 'Your Response')}</Label>
              <Textarea
                placeholder={t('requests.clarificationResponsePlaceholder', 'Provide your clarification response...')}
                value={resubmitComment}
                onChange={(e) => setResubmitComment(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResubmitDialogOpen(false); setResubmitComment(""); }} className="border-[#E0E0E0]">{t('common.cancel')}</Button>
            <Button
              onClick={() => {
                if (!selectedRequest) return;
                if (!resubmitComment.trim()) { toast.error(t('requests.provideResponse', 'Please provide a response')); return; }
                resubmitMutation.mutate({ requestId: selectedRequest, comments: resubmitComment });
              }}
              disabled={resubmitMutation.isPending || !resubmitComment.trim()}
              className="bg-[#B45309] hover:bg-[#92400E] text-white"
            >
              {resubmitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              {t('requests.resubmitRequest', 'Re-submit Request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
